import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import { UserAttributeKey, UserAttributes } from './interfaces'

import authManager from '../../api/security/Auth'

import { i18nInstance, StorageKeys, Translate2 } from 'common'
import { EmbeddedCommunicationsManager } from '../../api/embedded/EmbeddedCommunicationsManager'
import { Country } from '../../api/user/interfaces'
import UserDataServiceProvider from '../../services/UserDataServiceProvider'
import { cartSlice, isVehicleComplete, initCartMigration } from 'cart'
import { store } from 'store'
import { CatalogIdType } from '../../api/embedded/types/partsBasketSpec'
import {
  CatalogIndexTracker,
  CatalogLookupType,
} from '../../api/metrics/CatalogIndexTracker'
import { getAwsConfig } from '../../aws-exports'
import { TrackingManager } from '../../config/analytics/tracking'
import { buildVehicleStringDimension } from '../../config/analytics/tracking/utils'
import { CART_SESSION_KEY } from '../../config/analytics/UserActionsManager'
import {
  VehicleWidgetDisplayState,
  vehicleWidgetStore,
} from '../../features/search/VehicleSearch/store/VehicleWidgetStore'
import history from '../../helpers/history'
import { addUserToSentry } from '../../helpers/sentry'
import { storeInfo } from '../../helpers/StoreInfo'
import { hasVehicleInfoDetails } from '../../helpers/utils'
import AllianceConnectServiceProvider from '../../services/AllianceConnectServiceProvider'
import {
  DomType,
  OrgType,
  UuidType,
} from '../../services/attributes/AttributesModel'
import AttributesServiceProvider from '../../services/AttributesServiceProvider'
import { Region } from '../models/Regions'
import {
  SELECTED_VEHICLE_KEY,
  StockOptions,
  STORAGE_SEARCH_STATE_KEY,
} from '../search/SearchStore'
import { StoreInstances } from '../StoreInstancesContainer'
import SalesPadServiceProvider, {
  SalesPadOrg,
} from '../../services/SalesPadServiceProvider'
import UserInfoServiceProvider from '../../services/UserInfoServiceProvider'
import { ISendCodeResponse } from '../../features/login/interfaces'
import {
  IS_CCL_ENABLED,
  IS_INTEGRATED_VERSION,
} from '../../config/ConfigManager'
import CatalogServiceProvider from '../../services/CatalogServiceProvider'

export const UserStoreContext = createContext<UserStore | undefined>(undefined)
// https://stackoverflow.com/a/69735347
export const useUserStore = (): UserStore => {
  const userStore = useContext(UserStoreContext)
  if (!userStore) {
    throw new Error(
      'No UserStoreContext.Provider found when calling useUserStore.'
    )
  }
  return userStore
}

export enum MutablePreference {
  SEARCH_PREFERENCE = 'searchPreferences',
  SEARCH_PREFERENCE_VERSION = 'searchPreferencesVersion',
  EPEFEATURES_DEFAULTSTATE = 'epeFeatures_defaultState',
}

const defaultPreferences: UserAttributes = {
  language: '',
  defaultSort: '',
  defaultFilters: '',
  display_partsTypeAll: 'false',
  display_filtering: '',
  display_userFiltering: '',
  findit_partsProfessionalN: '',
  phoneNumber: '',
  findit_orderIfNotAvail: 'false',
  epeFeatures_buyersGuide: 'false',
  epeFeatures_defaultState: '',
  epeFeatures_labor: 'false',
  epeFeatures_plateToVin: 'false',
  EULA_accepted: '',
  orgName: '',
  userId: '',
  mclOrgId: '',
  orgId: '',
  wdorgId: '',
  searchPreferences: '',
  searchPreferencesVersion: '',
  searchPreferences_defaultSort: '',
  dds: '',
  cart_noteMaxSize: '' /* numeric */,
  cart_noteRequired: '' /* y/n */,
  cart_poMaxSize: '' /* numeric */,
  cart_pnMaxSize: '' /* numeric */,
  cart_poRequired: '' /* y/n */,
  partDomain: '',
  cart_multiCart: 'false',
  display_perCarQtyOne: 'false',
  screenName: '',
  courrierAcctId: '',
  treePath: '',
  shipmentStatusTrackerEnabled: 'false',
  cart_nonPrimaryLocWarningEnabled: 'false',
  cart_shipToOnCart: 'false',
  display_priceFld: '',
  promo_enabled: 'false',
  orderAllCarts: 'true',
  rma: '0',
  miscPreferences: '',
  cart_orderType: '',
  cart_validateOutOfStock: 'false',
  multiLocation: 'true',
  wdOrgName: '',
  display_multisellerLogos: 'true', // default value might change based off feeback
  bundlesEnabled: 'false',
  insufficientQtyWarning: 'false',
  displayExclTax: 'false',
}

export enum MiscPreferencesKeys {
  INTERCHANGE_PARTTYPES = 'I_ptype',
  INTERCHANGE_MANUFACTURERS = 'I_manufacturers',
  DEFAULT_VEHICLE_TYPE = 'defaultVehicleType',
  MULTI_SELLER_IDS_SORT_ORDER = 'multiSellerIdsSortOrder',
}

type MiscPreferences = Partial<Record<MiscPreferencesKeys, string>>

export class UserStore {
  private _resetCredentialsUserName?: string

  private _resetCredentialsDestination?: string

  displayName = ''

  utcOffset: number

  userHasLoaded = false

  loadingUser?: Promise<void>

  country?: Country

  userLogo?: string = undefined

  isImpersonating = false

  // User preferences, AKA: User Cache
  preferences: UserAttributes

  miscPreferences: MiscPreferences

  private _salesPadOrgs: SalesPadOrg[] = []

  activeCatalogPublisherId = 0
  regionId = 0
  decodeRegionId = null
  catalogRegionId = 0
  constructor() {
    this.preferences = defaultPreferences
    this.miscPreferences = JSON.parse(
      defaultPreferences.miscPreferences || '{}'
    )

    this.init()
    makeAutoObservable(this)
  }

  private init = async (): Promise<void> => {
    const date = new Date()
    this.utcOffset = date.getTimezoneOffset()
    let resolveLoadedUser:
      | ((value: void | PromiseLike<void>) => void)
      | undefined
    this.loadingUser = new Promise<void>((resolve) => {
      resolveLoadedUser = resolve
    })
    this.preferences = defaultPreferences
    this.miscPreferences = JSON.parse(
      defaultPreferences.miscPreferences || '{}'
    )
    this.setIsImpersonating(
      localStorage.getItem(StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY) === 'true'
    )

    await this.buildFromUserCache()
    TrackingManager.setUser(
      this.preferences?.userId,
      this.preferences?.partDomain
    )

    if (isVehicleComplete(StoreInstances?.searchStore?.currentVehicle)) {
      TrackingManager.setVehicle({
        year: StoreInstances?.searchStore?.currentVehicle.year.value,
        make: StoreInstances?.searchStore?.currentVehicle.make.value,
        model: StoreInstances?.searchStore?.currentVehicle.model.value,
        engine: StoreInstances?.searchStore?.currentVehicle.engine.value,
        id: buildVehicleStringDimension(
          StoreInstances?.searchStore?.currentVehicle,
          Region[this.country?.countryCode]
        ),
      })
    }
    if (resolveLoadedUser) {
      resolveLoadedUser()
    }
  }

  public set salesPadOrgs(orgs: SalesPadOrg[]) {
    this._salesPadOrgs = orgs
  }

  public get salesPadOrgs(): SalesPadOrg[] {
    return this._salesPadOrgs
  }

  public handleLogin = async (
    username: string,
    pass: string
  ): Promise<void> => {
    const u = await authManager.signIn(username, pass)
    if (!u) {
      throw new Error(Translate2('invalidCredentials'))
    }

    localStorage.removeItem(StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY)
    localStorage.removeItem(StorageKeys.AUTO_LOGIN_ORIGIN_KEY)
    localStorage.removeItem(StorageKeys.SALESPAD_KEY)
    localStorage.removeItem(StorageKeys.SAVED_VEHICLE_REGION_ID)
    await this.init()
  }

  public fetchSalesPadOrgs = async (): Promise<SalesPadOrg[]> => {
    const userId = authManager.getUserId()
    const orgId = this.preferences?.orgId
    return SalesPadServiceProvider.fetchSalesPadOrgs(userId, orgId)
  }

  public createSalesRepUserInfoCognito = async (
    orgId: number
  ): Promise<string> => {
    const userId = authManager.getUserId()
    return await UserInfoServiceProvider.createSalesRepUserInfoCognito(
      userId,
      orgId
    )
  }

  public sendCode = async (username: string): Promise<ISendCodeResponse> => {
    const resp = await AllianceConnectServiceProvider.forgotPasswordOtp({
      userName: username,
    })
    if (!resp) {
      throw new Error('errorSendingCode')
    }
    return resp
  }

  public resetPassword = async (
    code: string,
    username: string,
    newPassword: string
  ): Promise<void> => {
    const resp = await AllianceConnectServiceProvider.resetPassword({
      userName: username,
      password: newPassword,
      otp: code,
    })
    if (!resp) {
      throw new Error('Error resseting password')
    }
  }

  getIsImpersonating = (): boolean => {
    return this.isImpersonating
  }

  setIsImpersonating = (isImpersonating: boolean): void => {
    this.isImpersonating = isImpersonating
  }

  buildFromUserCache = async (): Promise<void> => {
    try {
      await authManager.getCurrentToken()
    } catch (_e) {
      this.setUserHasLoaded(true)
      return
    }
    try {
      const [userCache, country] = await Promise.all([
        authManager.getUserAttributes(),
        UserDataServiceProvider.getCountry(),
      ])

      // Load the app logo.
      UserDataServiceProvider.getUserLogo().then((userLogo) => {
        this.userLogo = userLogo
      })

      // Add user details to sentry.
      addUserToSentry({
        id: userCache.userId,
        username: userCache.screenName,
      })
      this.country = country

      const updatedPreferences: UserAttributes = {
        ...this.preferences,
        ...userCache,
      }
      this.setUserPreferences(updatedPreferences)

      initCartMigration(authManager.getUserId())
      store.dispatch(cartSlice.actions.restoreCart(authManager.getUserId()))
    } catch (e) {
      StoreInstances.uiStore.displayErrorNotification(`Amplify error: ${e}`)
    }
    if (IS_INTEGRATED_VERSION) {
      await this.decodeVehicle()
      await this.lookupBySearchInfo()
    } else {
      const selectedVehicleSessionStorage = JSON.parse(
        sessionStorage.getItem(SELECTED_VEHICLE_KEY) || '{}'
      )

      if (
        selectedVehicleSessionStorage &&
        Object.keys(selectedVehicleSessionStorage).length > 0
      ) {
        vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.view)
      } else {
        vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.select)
      }
    }

    try {
      const orgs = await this.fetchSalesPadOrgs()
      this.salesPadOrgs = orgs
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch salespad organizations:', e)
      this.salesPadOrgs = []
    }

    this.setUserHasLoaded(true)
    // Fetch and store contact info.
    const { parentOrgId, orgType, orgId } = this.preferences
    const contactId = orgType === OrgType.LOC ? parentOrgId : orgId
    if (contactId) {
      storeInfo.fetchContactAdressInfo(contactId)
    }

    if (!this.salesPadOrgs?.length) {
      StoreInstances.bannerPromotionStore.showBannerOnce()
    }
    CatalogServiceProvider.getActiveCatalogPublisher().then((data) => {
      this.activeCatalogPublisherId = data.activeCatalogPublisherId
      this.regionId = data.regionId
      this.catalogRegionId = data.catalogRegionId
    })
  }

  private async lookupBySearchInfo(): Promise<void> {
    const { catalogId, indexId, searchTxt } =
      EmbeddedCommunicationsManager.searchInfo || {}

    if (!searchTxt || !catalogId || !indexId) {
      return undefined
    }
    // Clear the previous search info to prevent it from being restored.
    sessionStorage.setItem(STORAGE_SEARCH_STATE_KEY, '')
    if (
      catalogId === CatalogIdType.AST ||
      catalogId === CatalogIdType.SAT ||
      catalogId === CatalogIdType.GSA
    ) {
      CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
      await StoreInstances.searchStore.searchByText(searchTxt)
      StoreInstances.searchStore.handleAddBreadCrumb({
        page: Translate2('allProducts'),
        breadcumb: searchTxt,
        url: () => StoreInstances.searchStore.searchByText(searchTxt),
      })
      history.navigate('/searchResults')
      return undefined
    }
    return undefined
  }

  private setUserHasLoaded = (userHasLoaded: boolean): void => {
    this.userHasLoaded = userHasLoaded
  }

  public updateUserPreferenceAndSyncToCloud = (
    key: MutablePreference,
    value: string
  ): void => {
    this.updateUserPreference(key, value)
    UserInfoServiceProvider.updateMiscPreferences([
      {
        key,
        value,
      },
    ]).catch(() => {
      // Do nothing.
    })
  }

  private updateUserPreference = (
    key: MutablePreference,
    value: string
  ): void => {
    const updatedPreferences = {
      ...this.preferences,
      [key]: value,
    }
    this.preferences = updatedPreferences
  }

  setUserPreferences = (preferences: UserAttributes): void => {
    this.preferences = preferences
    i18nInstance.setCurrentLanguage(preferences.language)
    try {
      this.miscPreferences = JSON.parse(preferences.miscPreferences || '{}')

      // Set default vehicle type if not present
      if (!this.miscPreferences[MiscPreferencesKeys.DEFAULT_VEHICLE_TYPE]) {
        this.miscPreferences[MiscPreferencesKeys.DEFAULT_VEHICLE_TYPE] = '111'
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      this.miscPreferences = {}
    }
  }

  /**
   * @deprecated because it depends on a method that causes unwanted side effects (reloads the entire app)
   * @param preferences
   */
  public updateMiscPreferences = (preferences: MiscPreferences): void => {
    const newMiscPreferences = { ...this.miscPreferences, ...preferences }

    const payloadAttributeValue = JSON.stringify(newMiscPreferences)
    const { userId } = StoreInstances.userStore.preferences
    const payload = {
      uuid: UuidType.MISCPREFERENCES_DEFAULTS,
      domId: Number(userId),
      domType: Number(DomType.USR),
      attrValue: payloadAttributeValue,
    }
    AttributesServiceProvider.saveOrUpdateAttribute([payload])
  }

  /**
   * @deprecated
   * @param uuidType
   * @param value
   */
  public saveRemotePreference = async (
    uuidType: UuidType,
    value: string
  ): Promise<void> => {
    const { userId } = StoreInstances.userStore.preferences
    const payload = {
      uuid: uuidType,
      domId: Number(userId),
      domType: Number(DomType.USR),
      attrValue: value,
    }
    await AttributesServiceProvider.saveAttribute([payload])
  }

  public saveRemoteMiscPreference = async (
    key: string,
    value: string
  ): Promise<void> => {
    const newMiscPreferences = {
      ...this.miscPreferences,
      [key]: value,
    }

    await this.saveRemotePreference(
      UuidType.MISCPREFERENCES_DEFAULTS,
      JSON.stringify(newMiscPreferences)
    )
  }

  public saveMiscPreference = (key: string, value: unknown): void => {
    const serializedValue = JSON.stringify(value)

    this.saveRemoteMiscPreference(key, serializedValue)

    // Update local miscPreferences state
    this.miscPreferences[key] = serializedValue
  }

  public saveStockFilterPreference = (selectedStockFilter: unknown): void => {
    const determineAttributeValue = (): string => {
      switch (selectedStockFilter) {
        case StockOptions.ONLY_PRICED:
          return '1'
        case StockOptions.ONLY_STOCKED:
          return '2'
        default:
          return '4'
      }
    }

    const value = determineAttributeValue()

    this.saveRemotePreference(UuidType.PARTS_USER_FILTER_LEVEL, value)

    // Update local preferences state
    this.preferences[UserAttributeKey.displayUserFiltering] = value
  }

  public getUserAttribute = (key: UserAttributeKey): string | undefined => {
    return this.preferences[key]
  }

  signOut = async (reloadPage = true): Promise<void> => {
    const awsConfig = getAwsConfig()
    const cognitoUserKeyBase = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}.${this.preferences?.userId}`
    await authManager.signOut()
    addUserToSentry(null)

    sessionStorage.clear()
    localStorage.removeItem(`${cognitoUserKeyBase}.deviceKey`)
    localStorage.removeItem(StorageKeys.BACKED_UP_SESSION)

    // TODO: need to check and delete the following localStorage keys:breadCrumbs,searches,bannerPromoDate,
    localStorage.removeItem(StorageKeys.AUTO_LOGIN_IMPERSONATION_KEY)
    const originToRedirect = localStorage.getItem(
      StorageKeys.AUTO_LOGIN_ORIGIN_KEY
    )
    localStorage.removeItem(StorageKeys.AUTO_LOGIN_ORIGIN_KEY)
    localStorage.removeItem(StorageKeys.SALESPAD_KEY)
    localStorage.removeItem(StorageKeys.ORDER_CONFIRMATION_KEY)

    localStorage.removeItem(StorageKeys.CXMLDETAILS_KEY)
    localStorage.removeItem(StorageKeys.CONDENSEDVIEW_KEY)
    localStorage.removeItem(CART_SESSION_KEY)
    localStorage.removeItem(StorageKeys.SAVED_VEHICLE_REGION_ID)
    this.setUserHasLoaded(false)
    this.setUserPreferences(defaultPreferences)

    if (originToRedirect) {
      window.location.href = originToRedirect
    } else {
      if (reloadPage) {
        // To prevent the current page being loaded after login.
        history.navigate('/login')
        setTimeout(() => {
          window.location.reload()
        })
      }
    }
  }

  getLanguage = (): string => {
    const locale = this.preferences?.language ?? 'en_US'
    return locale.split('_')[0]
  }

  public getUserLanguage(): string {
    return this.preferences?.language?.replace('_', '-') ?? 'en-US'
  }

  getStatePreference = (): string => {
    if (this.preferences?.epeFeatures_defaultState) {
      return this.preferences.epeFeatures_defaultState
    }
    return 'TX'
  }

  decodeVehicle = async (): Promise<void> => {
    if (
      IS_INTEGRATED_VERSION &&
      EmbeddedCommunicationsManager.vehicleInfo &&
      hasVehicleInfoDetails(EmbeddedCommunicationsManager.vehicleInfo)
    ) {
      // awaiting as we need to wait for the decoding to complete before moving further.
      await vehicleWidgetStore.lookupVehicle(
        EmbeddedCommunicationsManager.vehicleInfo
      )
    }
  }

  get resetCredentialsUserName(): string {
    return this._resetCredentialsUserName || ''
  }

  set resetCredentialsUserName(userName: string) {
    this._resetCredentialsUserName = userName
  }

  get resetCredentialsDestination(): string {
    return this._resetCredentialsDestination || ''
  }

  set resetCredentialsDestination(sentTo: string) {
    this._resetCredentialsDestination = sentTo
  }

  get isCclEnabled(): boolean {
    return this.activeCatalogPublisherId === 1 && IS_CCL_ENABLED
  }
}
